import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const AuthCallback = () => {
  const [status, setStatus] = useState('processing'); // processing, error
  const [errorInfo, setErrorInfo] = useState({ title: '', message: '' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check for error in URL hash
        const hash = window.location.hash;
        if (hash) {
          const params = new URLSearchParams(hash.substring(1));
          const error = params.get('error');
          const errorCode = params.get('error_code');
          const errorDescription = params.get('error_description');

          if (error) {
            handleAuthError(errorCode, errorDescription);
            return;
          }
        }

        // Check for error in query params
        const errorParam = searchParams.get('error');
        const errorDescParam = searchParams.get('error_description');
        if (errorParam) {
          handleAuthError(errorParam, errorDescParam);
          return;
        }

        // Get session - Supabase handles the token exchange automatically
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          handleAuthError('session_error', sessionError.message);
          return;
        }

        if (session) {
          // Check if user has completed onboarding
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/profile/me`, {
              headers: { 'Authorization': `Bearer ${session.access_token}` }
            }
          );
          
          if (response.ok) {
            const profile = await response.json();
            if (profile.is_onboarded) {
              navigate('/dashboard', { replace: true });
            } else {
              navigate('/onboarding', { replace: true });
            }
          } else if (response.status === 404 || response.status === 401) {
            // Profile doesn't exist yet, create it and go to onboarding
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({
                user_id: session.user.id,
                email: session.user.email
              })
            });
            navigate('/onboarding', { replace: true });
          } else {
            navigate('/onboarding', { replace: true });
          }
        } else {
          // No session found, wait a moment and try again
          setTimeout(async () => {
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (retrySession) {
              navigate('/onboarding', { replace: true });
            } else {
              handleAuthError('no_session', 'Unable to verify your login. Please try again.');
            }
          }, 1500);
        }
      } catch (err) {
        process.env.NODE_ENV === "development" && console.error('Auth callback error:', err);
        handleAuthError('unexpected', 'An unexpected error occurred. Please try again.');
      }
    };

    const handleAuthError = (errorCode, errorDescription) => {
      const errorMap = {
        'unauthorized_client': {
          title: 'Link Expired',
          message: 'Your magic link has expired or is invalid. Please request a new one.'
        },
        'otp_expired': {
          title: 'Link Expired',
          message: 'Your magic link has expired. Please request a new one.'
        },
        'access_denied': {
          title: 'Access Denied',
          message: 'Access was denied. Please try signing in again.'
        },
        'no_session': {
          title: 'Login Failed',
          message: errorDescription || 'Unable to complete login. Please try again.'
        }
      };

      const error = errorMap[errorCode] || {
        title: 'Authentication Error',
        message: errorDescription || 'Something went wrong. Please try again.'
      };

      setErrorInfo(error);
      setStatus('error');
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md text-center"
        >
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 
            className="text-2xl font-bold tracking-tight mb-3 text-foreground"
            data-testid="auth-error-title"
          >
            {errorInfo.title}
          </h1>
          <p 
            className="text-muted-foreground mb-8"
            data-testid="auth-error-message"
          >
            {errorInfo.message}
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-foreground text-background hover:bg-foreground/90"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        <Loader2 className="h-8 w-8 animate-spin text-foreground mx-auto mb-4" />
        <p className="text-muted-foreground" data-testid="auth-processing">
          Signing you in...
        </p>
      </motion.div>
    </div>
  );
};

export default AuthCallback;
